import type {
  ChannelLocationStatusDto,
  Location,
} from '@up2stay/up2promo-api-client';

export const GOOGLE_REVIEW_PROVIDER = 'google';

export type ReviewProviderKey = typeof GOOGLE_REVIEW_PROVIDER;

/**
 * Review-listing connection on a Location.
 * - `linked` — Channel Location sync (e.g. Business Profile OAuth) is active → healthy
 * - `listing` — external listing ID set (e.g. Google Place ID); sync not active → not green
 * - `needs_action` — no listing ID and no sync; not opted out → Signal Red
 * - `opt_out` — operator opted out of this provider → muted
 */
export type LocationConnectionStatus =
  | 'linked'
  | 'listing'
  | 'needs_action'
  | 'opt_out';

export type GoogleBusinessLocationOption = {
  placeId: string;
  googleBusinessLocationName: string;
  title?: string;
};

type LocationWithReviewExternalIds = Pick<Location, 'reviewExternalIds'>;

/**
 * Per-provider external listing ID from `location.reviewExternalIds`.
 * Linking a listing ID does not require Business Profile OAuth.
 */
export function getReviewListingId(
  location: LocationWithReviewExternalIds,
  provider: ReviewProviderKey,
): string | null {
  const id = location.reviewExternalIds?.[provider]?.trim();
  return id || null;
}

export function hasReviewListingId(
  location: LocationWithReviewExternalIds,
  provider: ReviewProviderKey,
): boolean {
  return getReviewListingId(location, provider) != null;
}

export function isProviderOptedOut(
  location: Pick<Location, 'reviewChannelOptOut'>,
  provider: ReviewProviderKey,
): boolean {
  return Boolean(location.reviewChannelOptOut?.[provider]);
}

export function channelForLocation(
  channels: ChannelLocationStatusDto[],
  locationId: string,
  provider: ReviewProviderKey,
): ChannelLocationStatusDto | undefined {
  return channels.find(
    (c) => c.locationId === locationId && c.provider === provider,
  );
}

export function isLocationLinked(
  channels: ChannelLocationStatusDto[],
  locationId: string,
  provider: ReviewProviderKey,
): boolean {
  return Boolean(channelForLocation(channels, locationId, provider)?.linked);
}

export function getLocationConnectionStatus(
  location: Pick<Location, 'id' | 'reviewExternalIds' | 'reviewChannelOptOut'>,
  channels: ChannelLocationStatusDto[],
  provider: ReviewProviderKey,
): LocationConnectionStatus {
  if (isLocationLinked(channels, location.id, provider)) return 'linked';
  if (isProviderOptedOut(location, provider)) return 'opt_out';
  if (hasReviewListingId(location, provider)) return 'listing';
  return 'needs_action';
}

export function countNeedsActionForProvider(
  locations: Pick<
    Location,
    'id' | 'reviewExternalIds' | 'reviewChannelOptOut'
  >[],
  channels: ChannelLocationStatusDto[],
  provider: ReviewProviderKey,
): number {
  return locations.filter(
    (loc) =>
      getLocationConnectionStatus(loc, channels, provider) === 'needs_action',
  ).length;
}

export function isProviderConnectionSetupComplete(
  locations: Pick<
    Location,
    'id' | 'reviewExternalIds' | 'reviewChannelOptOut'
  >[],
  channels: ChannelLocationStatusDto[],
  provider: ReviewProviderKey,
): boolean {
  if (locations.length === 0) return true;
  return countNeedsActionForProvider(locations, channels, provider) === 0;
}

/** Whether this GBP option is already linked to a different workspace location. */
export function resolveGoogleOptionAvailability(
  option: GoogleBusinessLocationOption,
  currentLocationId: string,
  channelLocations: ChannelLocationStatusDto[],
  locationNameById: Map<string, string>,
): { selectable: boolean; linkedToLocationName?: string } {
  for (const channel of channelLocations) {
    if (channel.locationId === currentLocationId) continue;
    const sameGbp =
      channel.providerLocationId === option.googleBusinessLocationName;
    const samePlace =
      channel.placeId != null &&
      channel.placeId !== '' &&
      channel.placeId === option.placeId;
    if (!sameGbp && !samePlace) continue;

    return {
      selectable: false,
      linkedToLocationName:
        locationNameById.get(channel.locationId) ?? channel.title ?? undefined,
    };
  }
  return { selectable: true };
}

export function partitionGoogleOptionsByAvailability(
  options: GoogleBusinessLocationOption[],
  currentLocationId: string,
  channelLocations: ChannelLocationStatusDto[],
  locationNameById: Map<string, string>,
): {
  selectable: GoogleBusinessLocationOption[];
  taken: Array<{
    option: GoogleBusinessLocationOption;
    linkedToLocationName?: string;
  }>;
} {
  const selectable: GoogleBusinessLocationOption[] = [];
  const taken: Array<{
    option: GoogleBusinessLocationOption;
    linkedToLocationName?: string;
  }> = [];

  for (const option of options) {
    const availability = resolveGoogleOptionAvailability(
      option,
      currentLocationId,
      channelLocations,
      locationNameById,
    );
    if (availability.selectable) {
      selectable.push(option);
    } else {
      taken.push({
        option,
        linkedToLocationName: availability.linkedToLocationName,
      });
    }
  }

  return { selectable, taken };
}

export function buildLocationNameById(
  locations: Pick<Location, 'id' | 'name'>[],
): Map<string, string> {
  return new Map(locations.map((loc) => [loc.id, loc.name]));
}

export function scoreGoogleLocationMatch(
  location: Pick<Location, 'name' | 'address' | 'reviewExternalIds'>,
  option: GoogleBusinessLocationOption,
): number {
  let score = 0;
  const listingId = getReviewListingId(location, GOOGLE_REVIEW_PROVIDER);
  if (listingId && option.placeId && listingId === option.placeId) {
    score += 10;
  }

  const name = location.name?.trim().toLowerCase() ?? '';
  const title = option.title?.trim().toLowerCase() ?? '';
  if (name && title) {
    if (title === name) score += 5;
    else if (title.includes(name) || name.includes(title)) score += 3;
  }

  const address = location.address?.trim().toLowerCase() ?? '';
  if (address && title) {
    const firstLine = address.split(',')[0]?.trim() ?? '';
    if (firstLine && title.includes(firstLine)) score += 2;
  }

  return score;
}

export function partitionGoogleLocations(
  location: Pick<Location, 'name' | 'address' | 'reviewExternalIds'>,
  options: GoogleBusinessLocationOption[],
  search: string,
): {
  suggested: GoogleBusinessLocationOption[];
  other: GoogleBusinessLocationOption[];
} {
  const query = search.trim().toLowerCase();
  const filtered = query
    ? options.filter((option) => {
        const title =
          option.title?.toLowerCase() ??
          option.googleBusinessLocationName.toLowerCase();
        return (
          title.includes(query) || option.placeId.toLowerCase().includes(query)
        );
      })
    : options;

  const scored = filtered
    .map((option) => ({
      option,
      score: scoreGoogleLocationMatch(location, option),
    }))
    .sort((a, b) => b.score - a.score);

  const suggested = scored
    .filter((entry) => entry.score > 0)
    .slice(0, 3)
    .map((entry) => entry.option);

  const suggestedKeys = new Set(
    suggested.map((s) => s.googleBusinessLocationName),
  );
  const other = filtered.filter(
    (option) => !suggestedKeys.has(option.googleBusinessLocationName),
  );

  return { suggested, other };
}
